import type {
  StylesBuilderSelections,
  PageType,
  LayoutType,
  StyleType,
  ThemeType,
  AccentColor,
  TypographyType,
  SpacingType,
  AnimationType,
} from "@/store/zustand/stylesBuilderStore";
import { ACCENT_COLOR_OPTIONS } from "@/store/zustand/stylesBuilderStore";
import { generatePromptContent } from "./stylesBuilderRenderer";
import type { StyleSelections } from "./styleResolver";

const PAGE_TYPE_TEXT: Record<PageType, string> = {
  landing:
    "Landing page structure: Include a prominent hero section with headline, subheadline, and primary CTA button. Add a features/benefits section with icon cards or columns (3-4 items). Include social proof section (testimonials, logos, or stats). Add a secondary CTA section. Include a simple footer with links.",
  dashboard:
    "Dashboard layout: Include a top navigation bar with user menu and notifications. Add a collapsible sidebar with navigation links and icons. Create a main content area with: metric cards showing KPIs (4-6 cards), data visualization charts (line charts, bar charts, or pie charts), data tables with sorting and filtering capabilities, and an activity feed or recent items panel.",
  settings:
    "Settings page structure: Use a left sidebar or tab navigation for settings categories. Organize settings into logical groups (Profile, Account, Notifications, Security, Preferences). Use form controls: text inputs, toggles, dropdowns, radio groups. Include save/cancel buttons for each section. Add danger zone section for destructive actions.",
  onboarding:
    "Onboarding flow: Create a multi-step wizard with progress indicator (steps or progress bar). Center content with clear headings for each step. Include form fields appropriate to each step. Add back/next navigation buttons. Include skip option where appropriate. Final step should have a completion celebration or summary.",
  ecommerce:
    "E-commerce layout: Include a header with logo, search bar, cart icon with count, and user menu. Add category navigation. Create product grid with: product cards showing image, title, price, and rating. Include filter sidebar (price range, categories, attributes). Add sorting dropdown. Include pagination or infinite scroll. Product cards should have hover states and quick-add buttons.",
  blog: "Blog layout: Include a header with logo and navigation. Create article cards with: featured image, title, excerpt, author, date, and read time. Add category/tag filters. Include a featured or hero post section. Add sidebar with: search, categories, popular posts, newsletter signup. Use a clean, readable layout optimized for content consumption.",
  docs: "Documentation site: Include a persistent left sidebar with hierarchical navigation (collapsible sections). Add a top navigation bar with search and version selector. Main content area with: breadcrumbs, article title, table of contents (right sidebar), markdown-style content with headings, code blocks, and callouts. Include previous/next navigation at bottom. Add copy button for code blocks.",
  portfolio:
    "Portfolio layout: Include a hero section with name, title, and brief intro. Add a project gallery with: project cards showing thumbnail, title, and tech stack. Include filterable categories. Add an about section with bio and skills. Include contact section with form or links. Add subtle animations for visual interest. Consider a case study layout for detailed project pages.",
};

const LAYOUT_TEXT: Record<LayoutType, string> = {
  "single-column":
    "Single column centered layout: Content centered with max-width constraint (typically 720-900px). Full-width sections can break out for visual variety. Good for reading-focused content, forms, and simple flows. Stack elements vertically with consistent spacing.",
  "sidebar-left":
    "Left sidebar layout: Fixed or sticky sidebar (200-280px width) for navigation or filters. Main content area takes remaining space. Sidebar should be collapsible on mobile (hamburger menu). Use clear visual separation between sidebar and content.",
  "sidebar-right":
    "Right sidebar layout: Main content on left, supplementary sidebar on right (240-320px). Right sidebar for: table of contents, related items, ads, or contextual info. Hide sidebar on mobile or move below content. Main content should be primary focus.",
  grid: "Grid-based layout: Use CSS Grid or Flexbox for card arrangements. Responsive grid: 1 column mobile, 2-3 columns tablet, 3-4 columns desktop. Consistent card sizing with uniform gaps (16-24px). Cards should have consistent structure and equal heights per row.",
  "full-width-sections":
    "Full-width stacked sections: Each section spans full viewport width. Alternate section backgrounds for visual rhythm (light/dark or colored/white). Content within sections should be constrained to max-width. Good for landing pages and marketing sites. Use generous vertical padding between sections (80-120px).",
};

const STYLE_TEXT: Record<StyleType, string> = {
  minimal:
    "Minimal aesthetic: Use generous whitespace and breathing room. Subtle shadows (shadow-sm) or no shadows. Thin borders (1px) in muted colors. Limited color palette - primarily neutrals with single accent. Clean, uncluttered interfaces. Hide non-essential elements. Use typography hierarchy instead of decorative elements.",
  bold: "Bold and vibrant design: Use strong, saturated colors. Larger text sizes and heavier font weights. Prominent buttons with solid backgrounds. High contrast between elements. Consider dark backgrounds with bright accent colors. Use color blocks and filled shapes. Make CTAs and important elements stand out dramatically.",
  soft: "Soft and rounded UI: Use large border radius (12-20px) on cards and buttons. Soft shadows (shadow-md with low opacity). Pastel or muted color palette. Rounded avatars and icons. Gentle gradients. Smooth, organic feeling. Consider pill-shaped buttons and tags. Light, airy backgrounds.",
  glass:
    "Glassmorphism style: Use translucent backgrounds (bg-white/10 or bg-black/10). Apply backdrop blur (backdrop-blur-md or backdrop-blur-lg). Subtle borders (border-white/20). Layer elements with depth. Works best on gradient or image backgrounds. Use subtle shadows for depth. Frosted glass effect on cards and modals.",
  elegant:
    "Elegant and sophisticated: Refined typography with careful kerning and line-height. Subtle color palette - blacks, whites, golds, or deep jewel tones. Delicate borders and dividers. Tasteful use of serif fonts for headings. Generous spacing. Understated animations. Premium, luxurious feel. Quality over quantity in visual elements.",
  corporate:
    "Professional corporate style: Clean, structured grid layouts. Neutral color palette (grays, whites, navy, subtle blues). Clear visual hierarchy. Conventional, familiar UI patterns. Data-focused components. Professional iconography. Consistent spacing system. Accessible and usable by default. Trustworthy, reliable appearance.",
};

const THEME_TEXT: Record<ThemeType, string> = {
  light:
    "Light theme: White or light gray backgrounds (#FFFFFF, #FAFAFA, #F5F5F5). Dark text for readability (#111827, #374151). Subtle borders in gray (#E5E7EB). Cards slightly elevated from background. Use shadows for depth. Accent colors should be vibrant against light backgrounds.",
  dark: "Dark theme: Dark backgrounds (#0A0A0A, #111111, #18181B). Light text (#F9FAFB, #E5E7EB). Subtle borders (#27272A, #3F3F46). Cards using slightly lighter surface colors. Reduce shadow-sm usage, use borders or subtle gradients instead. Accent colors may need to be slightly desaturated for comfort. Consider reducing pure white text to #E5E7EB for less eye strain.",
  both: "Support both light and dark themes: Implement a theme toggle or respect system preference (prefers-color-scheme). Define CSS variables or Tailwind dark: variants for all colors. Light theme: white/light gray backgrounds, dark text. Dark theme: dark backgrounds (#0A0A0A-#18181B), light text. Ensure sufficient contrast in both modes. Test all components in both themes.",
};

const TYPOGRAPHY_CATEGORY_TEXT = {
  sans: "Modern sans-serif typography: Clean, geometric letterforms. Font weights: 400 for body, 500 for emphasis, 600-700 for headings. Line height: 1.5-1.6 for body text, 1.2-1.3 for headings. Letter spacing: slight negative for large headings (-0.02em), normal for body. Size scale: 14px body, 16px large body, 24-48px for headings.",
  serif:
    "Classic serif typography: Elegant letterforms with serifs for headings. Pair with clean sans-serif for body text. Serif headings convey elegance and tradition. Font weights: 400-700 for serif headings. Slightly increased line-height for serif body text (1.6-1.8). Good for editorial, luxury, and traditional brands.",
  mono: "Monospace typography: Fixed-width characters for technical aesthetic. Best for: code blocks, technical content, terminal UIs, or unique stylistic choice. Consider monospace for headings with sans-serif body for readability. Slightly reduced font size for monospace body (-1px). Technical, developer-focused, or retro-modern aesthetic.",
};

const TYPOGRAPHY_TEXT: Record<TypographyType, string> = {
  "space-grotesk": `Use Space Grotesk font. ${TYPOGRAPHY_CATEGORY_TEXT.sans}`,
  "bricolage-grotesque": `Use Bricolage Grotesque font. ${TYPOGRAPHY_CATEGORY_TEXT.sans}`,
  "ibm-plex-sans": `Use IBM Plex Sans font. ${TYPOGRAPHY_CATEGORY_TEXT.sans}`,
  "source-sans": `Use Source Sans 3 font. ${TYPOGRAPHY_CATEGORY_TEXT.sans}`,
  playfair: `Use Playfair Display font. ${TYPOGRAPHY_CATEGORY_TEXT.serif}`,
  "crimson-pro": `Use Crimson Pro font. ${TYPOGRAPHY_CATEGORY_TEXT.serif}`,
  newsreader: `Use Newsreader font. ${TYPOGRAPHY_CATEGORY_TEXT.serif}`,
  "jetbrains-mono": `Use JetBrains Mono font. ${TYPOGRAPHY_CATEGORY_TEXT.mono}`,
  "fira-code": `Use Fira Code font. ${TYPOGRAPHY_CATEGORY_TEXT.mono}`,
  "ibm-plex-mono": `Use IBM Plex Mono font. ${TYPOGRAPHY_CATEGORY_TEXT.mono}`,
};

const SPACING_TEXT: Record<SpacingType, string> = {
  compact:
    "Compact spacing: Reduced padding and margins throughout. Card padding: 12-16px. Section gaps: 16-24px. Element spacing: 8-12px. Good for data-dense interfaces, dashboards, and admin panels. Prioritize information density. Use smaller text sizes where appropriate. Tighter line-height (1.4-1.5).",
  comfortable:
    "Comfortable spacing: Balanced padding and margins. Card padding: 16-24px. Section gaps: 32-48px. Element spacing: 12-16px. Default choice for most applications. Good balance between density and readability. Line-height: 1.5-1.6. Breathing room without feeling sparse.",
  spacious:
    "Spacious layout: Generous padding and margins throughout. Card padding: 24-32px. Section gaps: 64-96px. Element spacing: 16-24px. Good for marketing sites, portfolios, and content-focused pages. Creates premium, unhurried feel. Larger text sizes. Line-height: 1.6-1.8. Let content breathe.",
};

const ANIMATION_TEXT: Record<AnimationType, string> = {
  none: "No animations: Static UI without transitions or animations. Instant state changes. Focus purely on layout and static design. Suitable for accessibility preferences (prefers-reduced-motion) or performance-critical applications.",
  subtle:
    "Subtle animations: Smooth hover transitions (150-200ms ease). Button hover: slight scale or background color shift. Card hover: subtle shadow-sm increase or border highlight. Focus states: smooth ring-3 animation. Page transitions: simple fade (200ms). Loading states: gentle pulse or skeleton screens. Use transform and opacity for performance.",
  rich: "Rich animations: Page transitions with slide or fade effects (300-500ms). Scroll-triggered animations: elements fade/slide in as they enter viewport. Micro-interactions: button clicks with satisfying feedback, toggle animations, success celebrations. Loading: skeleton screens with shimmer effect. Staggered list animations. Consider using Framer Motion or similar. Spring physics for natural feel. Respect prefers-reduced-motion.",
};

const ACCENT_COLOR_USAGE =
  "Apply accent color to: primary buttons, links, focus rings, active states, selected items, progress indicators, toggles when on, important badges or tags, and key interactive elements. Use sparingly for maximum impact - accent should draw attention to primary actions.";

export function selectionsToPromptText(
  selections: StylesBuilderSelections,
  customAccentHex?: string | null,
): string {
  const lines: string[] = [];

  if (selections.pageType) {
    lines.push(`- Page type: ${PAGE_TYPE_TEXT[selections.pageType]}`);
  }

  if (selections.layout) {
    lines.push(`- Layout: ${LAYOUT_TEXT[selections.layout]}`);
  }

  if (selections.style) {
    lines.push(`- Visual style: ${STYLE_TEXT[selections.style]}`);
  }

  if (selections.theme) {
    lines.push(`- Theme: ${THEME_TEXT[selections.theme]}`);
  }

  if (customAccentHex) {
    lines.push(`- Accent color: ${customAccentHex}. ${ACCENT_COLOR_USAGE}`);
  } else if (selections.accentColor) {
    const colorOption = ACCENT_COLOR_OPTIONS.find(
      (c) => c.value === selections.accentColor,
    );
    const hexCode = colorOption?.hex || "";
    lines.push(
      `- Accent color: ${selections.accentColor} (${hexCode}). ${ACCENT_COLOR_USAGE}`,
    );
  }

  if (selections.typography) {
    lines.push(`- Typography: ${TYPOGRAPHY_TEXT[selections.typography]}`);
  }

  if (selections.spacing) {
    lines.push(`- Spacing: ${SPACING_TEXT[selections.spacing]}`);
  }

  if (selections.animation) {
    lines.push(`- Animations: ${ANIMATION_TEXT[selections.animation]}`);
  }

  if (lines.length === 0) {
    return "";
  }

  return `\n\n## UI/UX Requirements (User-Specified)\nThe following design specifications must be followed:\n${lines.join("\n")}`;
}

/**
 * Checks if any meaningful selections have been made
 */
export function hasAnySelections(selections: StylesBuilderSelections): boolean {
  return Object.values(selections).some((value) => value !== null);
}

/**
 * Gets a short summary of selections for display purposes
 * (e.g., "Dark, Minimal, Landing Page")
 */
export function getSelectionsSummary(
  selections: StylesBuilderSelections,
): string {
  const parts: string[] = [];

  if (selections.theme) {
    parts.push(selections.theme === "dark" ? "Dark" : "Light");
  }

  if (selections.style) {
    parts.push(
      selections.style.charAt(0).toUpperCase() + selections.style.slice(1),
    );
  }

  if (selections.pageType) {
    const pageLabels: Record<PageType, string> = {
      landing: "Landing",
      dashboard: "Dashboard",
      settings: "Settings",
      onboarding: "Onboarding",
      ecommerce: "E-commerce",
      blog: "Blog",
      docs: "Docs",
      portfolio: "Portfolio",
    };
    parts.push(pageLabels[selections.pageType]);
  }

  return parts.join(" · ") || "No style preferences";
}

function toStyleSelections(
  selections: StylesBuilderSelections,
  customAccentHex?: string | null,
): StyleSelections {
  return {
    style: selections.style,
    theme: selections.theme,
    accentColor: selections.accentColor,
    typography: selections.typography,
    spacing: selections.spacing,
    animation: selections.animation,
    cornerStyle: selections.cornerStyle,
    shadow: selections.shadow,
    customAccentHex,
  };
}

/**
 * Generates both system prompt text and additional instructions with HTML examples.
 *
 * This function uses the unified renderer to ensure the HTML examples shown in the
 * preview are the same as what the AI receives as reference.
 *
 * @param selections - The current prompt builder selections
 * @param customAccentHex - Optional custom hex color for accent
 * @returns Object containing systemPromptText and additionalInstructions
 */
export function generateFullPromptContent(
  selections: StylesBuilderSelections,
  customAccentHex?: string | null,
): { systemPromptText: string; additionalInstructions: string } {
  const styleSelections = toStyleSelections(selections, customAccentHex);
  return generatePromptContent(styleSelections);
}

/**
 * Gets just the additional instructions (HTML examples) for the AI.
 * This is intended to be added to the system prompt's additional instructions
 * section, which is hidden from the user.
 *
 * @param selections - The current prompt builder selections
 * @param customAccentHex - Optional custom hex color for accent
 * @returns HTML examples as additional instructions string
 */
export function getAdditionalInstructions(
  selections: StylesBuilderSelections,
  customAccentHex?: string | null,
): string {
  const { additionalInstructions } = generateFullPromptContent(
    selections,
    customAccentHex,
  );
  return additionalInstructions;
}
